/** @format */

import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';

import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';
import { ShippingOptionButtonComponent } from '../shipping-option-button/shipping-option-button.component';
import { shippingOptions } from '@plp/data-access/constants';
import type { FulfillmentLocationInfo } from '@plp/data-access/interfaces';

@Component({
  selector: 'plp-components-fulfillment-filter-chip',
  imports: [ShippingOptionButtonComponent, TitleCasePipe, HmfActionTextComponent, NgTemplateOutlet],
  templateUrl: './fulfillment-filter-chip.component.html',
  styleUrl: './fulfillment-filter-chip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FulfillmentFilterChipComponent {
  readonly sameDayDeliveryEnabled = input(false);
  readonly fulfillmentFacetsEnabled = input(false);
  readonly exposedFiltersEnabled = input(false);
  readonly moveFulfillmentToFilters = input(false);
  readonly mobileSelectionPosition = input<'top' | 'left'>('top');
  readonly isMobile = input(false);
  readonly disabled = input(false);
  readonly locationInfo = input<FulfillmentLocationInfo | null>(null);

  readonly fulfillmentFacets = input<Record<string, number>>({});

  readonly selectedOption = output<string>();
  readonly changeLocationAndStoreName = output<'pickup' | 'ship'>();

  readonly hasFulfillmentData = computed(() => Object.keys(this.fulfillmentFacets()).length > 0);

  readonly showMobileSelection = computed((): boolean => {
    return this.isMobile() && (!this.sameDayDeliveryEnabled() || !!this.locationInfo());
  });

  shippingOptions = shippingOptions;
  private readonly ALL_SHIPPING_KEYS = Object.keys(shippingOptions);

  // Filter keys: if SDD enabled, remove 'Shopping In-Store' else remove 'Same Day Delivery'
  // Ship is in its own container if SDD flag is off.
  readonly shippingOptionsKeys = computed(() => {
    const isSDD = this.sameDayDeliveryEnabled() ?? false;
    const keys = this.ALL_SHIPPING_KEYS.filter((key): key is keyof typeof shippingOptions =>
      isSDD ? key !== 'ISA' : key !== 'SDD',
    );
    // When moving fulfillment into filters, reorder from ship -> pickup -> sdd to pickup -> sdd -> ship.
    if (this.moveFulfillmentToFilters()) {
      return [...keys].sort((a, b) => {
        const order = { BOPIS: 0, ISA: 1, SDD: 1, SHIP: 2 } as Record<string, number>;
        return (order[a] ?? 0) - (order[b] ?? 0);
      });
    }
    return keys;
  });

  setSelectedFilter(option: string): void {
    this.selectedOption.emit(option);
  }
}
